import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useIsMobile } from '../lib/useIsMobile.js';
import { cellText } from '../lib/format.js';
import { visibleColumns } from '../lib/visibleFields.mjs';

// Repeat cadence in plain language, stored as the cooldown in minutes (mirrors
// AlertEditor). This sheet keeps the presets only; custom "every X days" lives
// in the full editor.
const CADENCES = { hourly: 60, daily: 1440, weekly: 10080, monthly: 43200 };
const CADENCE_OF = (min) => Object.keys(CADENCES).find((k) => CADENCES[k] === Number(min)) || 'daily';
const CADENCE_TEXT = (min) => ({ hourly: 'once an hour', daily: 'once a day', weekly: 'once a week', monthly: 'once a month' }[CADENCE_OF(min)]);

// 🔔 "Create alert" from a dashboard tile — the in-context sheet.
//
// The whole point is NOT to be the Alerts editor. The heavy editor (/alerts →
// AlertEditor) exists for building a watcher from scratch: pick a dashboard, pick
// a tile or build a metric, choose channels, quiet hours, templates… Here the user
// is ALREADY looking at the number, so everything about *what* to watch is already
// known — the sheet asks the one question that's left ("tell me when it does
// what?") and gets out of the way. Anything finer is one tap away in Alerts.
//
// Pre-filled from the tile: the dashboard + tile ref, the tile's title (→ the
// alert name), its live on-screen value (→ "use as the number") and a unit guessed
// from how the tile renders (money / percent / count). The saved alert is a normal
// tile-source alert, so it lists, edits, pauses and fires exactly like any other.
//
// Mobile-first: on a phone it is a FULL-SCREEN sheet (portaled to <body>, so a
// grid item's transform can't trap it), one column, 44px controls, sticky action
// bar above the safe area. On desktop it's a centred card.
//
// Dual-surface by construction: the same component serves a client self-serving
// and a Howler admin looking at that client's dashboard — the server decides who
// may write (alerts.manage), and it reports that back as `canManage`.
export default function TileAlertModal({ tile, data, suiteId, suiteName = '', dashboardId, onClose }) {
  const isMobile = useIsMobile();
  const guess = useMemo(() => tileMetric(tile, data), [tile, data]);

  const [ctx, setCtx] = useState(null);          // server context (permission, channels, existing)
  const [loadErr, setLoadErr] = useState('');
  const [ruleType, setRuleType] = useState('threshold'); // threshold | depletion | sold_out
  const [operator, setOperator] = useState('gte');
  const [threshold, setThreshold] = useState('');
  const [unit, setUnit] = useState(guess.unit);
  const [name, setName] = useState(tile?.title ? String(tile.title).slice(0, 120) : '');
  const [channels, setChannels] = useState(['push']);
  const [more, setMore] = useState(false);        // the fine-tune disclosure
  const [priority, setPriority] = useState('normal');
  const [frequency, setFrequency] = useState('once');
  const [cooldownMin, setCooldownMin] = useState('1440'); // at most once a day
  const [tag, setTag] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(null);       // the created alert → success state

  // Lock the page behind the sheet + close on Escape (mirrors TileFullscreen).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  // Who may create one here, which channels this client actually has, and what is
  // already watching this tile.
  const tileId = tile?.id || '';
  useEffect(() => {
    let alive = true;
    if (!suiteId || !dashboardId || !tileId) return undefined;
    api.tileAlertContext(suiteId, dashboardId, tileId)
      .then((r) => { if (alive) setCtx(r); })
      .catch((e) => { if (alive) setLoadErr(e.message || 'Could not open the alert setup here.'); });
    return () => { alive = false; };
  }, [suiteId, dashboardId, tileId]);

  const canManage = ctx ? !!ctx.canManage : false;
  const eventName = ctx?.suiteName || suiteName;
  const metricName = ctx?.tileName || tile?.title || 'this number';

  // The plain-English read-back, so the rule is never a mystery before saving.
  const sentence = () => {
    const t = threshold === '' ? '…' : fmtNum(Number(threshold), unit);
    const when = ruleType === 'sold_out' ? `${metricName} sells out`
      : ruleType === 'depletion' ? `${metricName} drops below ${t}`
        : `${metricName} ${operator === 'lte' ? 'drops to' : 'reaches'} ${t}`;
    const via = ['your inbox', ...channels.map((c) => ({ push: 'push', email: 'email', sms: 'SMS', slack: 'Slack' }[c]))].filter(Boolean);
    const viaTxt = via.length > 1 ? `${via.slice(0, -1).join(', ')} and ${via[via.length - 1]}` : via[0];
    return `When ${when}, tell me via ${viaTxt} — ${frequency === 'once' ? 'once' : `every time, at most ${CADENCE_TEXT(cooldownMin)}`}.`;
  };

  const toggleChannel = (c) => setChannels((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));

  async function save() {
    if (ruleType !== 'sold_out' && (threshold === '' || Number.isNaN(Number(threshold)))) { setErr('Set the number to alert on.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await api.createTileAlert(suiteId, {
        dashboardId, tileId,
        name: (name || '').trim() || `${metricName} alert`,
        ruleType,
        operator: ruleType === 'threshold' ? operator : 'lte',
        threshold: ruleType === 'sold_out' ? 0 : Number(threshold),
        unit, tag: tag.trim(), channels,
        priority, frequency, cooldownMin: Number(cooldownMin) || 60,
      });
      if (r?.error) throw new Error(r.error);
      setSaved(r.alert);
    } catch (e) { setErr(e.message || 'Could not save the alert.'); }
    finally { setBusy(false); }
  }

  const body = (
    <div style={sheet(isMobile)} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Create an alert from this tile">
      <div style={head(isMobile)}>
        <span style={{ fontSize: 19 }}>🔔</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>{saved ? 'Alert set' : 'Create alert'}</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {metricName}{eventName ? ` · ${eventName}` : ''}
          </div>
        </div>
        <button onClick={onClose} style={xBtn} aria-label="Close">✕</button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '14px 16px' : '14px 18px' }}>
        {/* ── Saved: confirm + the one link out (editable in Alerts, as promised) ── */}
        {saved ? (
          <div>
            <div style={okBox}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>✅ Pulse is watching {metricName}.</div>
              <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>{sentence()}</div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 12, lineHeight: 1.5 }}>
              It’s checked every few minutes and always lands in your Pulse inbox. Fine-tune it
              (quiet hours, SMS numbers, a test fire) any time on the Alerts page.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <Link to="/alerts" style={{ ...btnGhost, textDecoration: 'none', textAlign: 'center' }} onClick={onClose}>Manage in Alerts →</Link>
              <button onClick={onClose} style={btnPrimary}>Done</button>
            </div>
          </div>
        ) : loadErr ? (
          <div style={{ fontSize: 13, color: 'var(--error, #dc2626)', lineHeight: 1.5 }}>{loadErr}</div>
        ) : !ctx ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[70, 100, 60].map((w, i) => <div key={i} className="skel" style={{ width: `${w}%`, height: 34, borderRadius: 10 }} />)}
          </div>
        ) : !canManage ? (
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.55 }}>
            You don’t have permission to create alerts for this event. Ask an owner on your team
            (or your Howler account manager) to set it up — they can do it from this same tile.
          </div>
        ) : (<>
          {/* What it reads right now — straight off the tile on screen, no extra query. */}
          <div style={readsBox}>
            <span style={{ color: 'var(--muted)', fontWeight: 600 }}>Now:</span>
            <b style={{ fontSize: 15 }}>{guess.display || '—'}</b>
            {guess.value != null && ruleType !== 'sold_out' && (
              <>
                <span style={{ flex: 1 }} />
                <button type="button" onClick={() => setThreshold(String(Math.round(guess.value)))} style={miniBtn}>Use as the number</button>
              </>
            )}
          </div>

          {/* Anything already watching this tile — no silent duplicates. */}
          {!!(ctx.existing || []).length && (
            <div style={{ fontSize: 11.5, color: 'var(--muted)', margin: '8px 2px 0', lineHeight: 1.45 }}>
              Already watching this tile: {ctx.existing.map((a) => a.name).join(', ')}.{' '}
              <Link to="/alerts" onClick={onClose} style={{ color: 'var(--brand)', fontWeight: 700 }}>Manage</Link>
            </div>
          )}

          <Field label="Tell me when…">
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 8 }}>
              <Seg active={ruleType === 'threshold'} onClick={() => setRuleType('threshold')}>📈 It reaches a number</Seg>
              <Seg active={ruleType === 'depletion'} onClick={() => setRuleType('depletion')}>⚠️ It drops below</Seg>
              <Seg active={ruleType === 'sold_out'} onClick={() => { setRuleType('sold_out'); setThreshold('0'); }}>🎉 It sells out</Seg>
            </div>
          </Field>

          {ruleType !== 'sold_out' ? (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {ruleType === 'threshold' && (
                <Field label="Direction" style={{ flex: '1 1 140px' }}>
                  <select value={operator} onChange={(e) => setOperator(e.target.value)} style={inp}>
                    <option value="gte">Rises to / above ↑</option>
                    <option value="lte">Drops to / below ↓</option>
                  </select>
                </Field>
              )}
              <Field label="Number" style={{ flex: '1 1 120px' }}>
                <input value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="e.g. 5000" inputMode="decimal" style={inp} autoFocus={!isMobile} />
              </Field>
              <Field label="Unit" style={{ flex: '0 0 108px' }}>
                <select value={unit} onChange={(e) => setUnit(e.target.value)} style={inp}>
                  {[...new Set(['tickets', 'ZAR', '%', 'orders', 'count', unit])].map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </Field>
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--muted)', margin: '0 2px 12px', lineHeight: 1.45 }}>
              Fires the moment this number hits zero — the big one. No threshold to set.
            </div>
          )}

          <Field label="How should we tell you?" hint="It always lands in your Pulse inbox. Add any of these on top.">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Chip active={channels.includes('push')} onClick={() => toggleChannel('push')}>📱 Push</Chip>
              <Chip active={channels.includes('email')} onClick={() => toggleChannel('email')}>✉️ Email</Chip>
              <Chip active={channels.includes('sms')} onClick={() => toggleChannel('sms')} disabled={!ctx.smsAvailable} title={ctx.smsAvailable ? 'Add numbers on the Alerts page' : 'SMS isn’t configured for this client yet'}>💬 SMS</Chip>
              <Chip active={channels.includes('slack')} onClick={() => toggleChannel('slack')} disabled={!ctx.slackAvailable} title={ctx.slackAvailable ? '' : 'Slack isn’t connected for this client yet'}># Slack</Chip>
            </div>
          </Field>

          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={`${metricName} alert`} style={inp} />
          </Field>

          {/* Everything else has a sane default — hidden until asked for, so the
              common path stays "one number, Create". */}
          <button type="button" onClick={() => setMore((v) => !v)} style={discloseBtn}>{more ? '▾' : '▸'} Fine-tune (importance, repeats, category)</button>
          {more && (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Field label="Importance" hint="Important alerts ignore quiet hours." style={{ flex: '1 1 150px' }}>
                  <select value={priority} onChange={(e) => setPriority(e.target.value)} style={inp}>
                    <option value="normal">Normal</option>
                    <option value="important">Important (always reach me)</option>
                  </select>
                </Field>
                <Field label="How often" style={{ flex: '1 1 150px' }}>
                  <select value={frequency} onChange={(e) => setFrequency(e.target.value)} style={inp}>
                    <option value="once">Once, then rest</option>
                    <option value="repeat">Every time it crosses</option>
                  </select>
                </Field>
              </div>
              {frequency === 'repeat' && (
                <Field label="Remind me at most" hint="The least time between repeats, so a condition that stays true can’t alert you on every check.">
                  <select value={CADENCE_OF(cooldownMin)} onChange={(e) => setCooldownMin(String(CADENCES[e.target.value] || 1440))} style={inp}>
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly (30 days)</option>
                  </select>
                </Field>
              )}
              <Field label="Category (optional)" hint="Groups this alert on the Alerts page (Ticketing, Cashless…).">
                <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="e.g. Ticketing" style={inp} />
              </Field>
            </div>
          )}

          <div style={sentenceBox}>{sentence()}</div>
          {/* Be straight about WHAT gets watched: the tile as this event has it set
              up (its own filters + the event's locks — the same read goals use), not
              a filter you happen to be trying out on the dashboard right now. */}
          <div style={{ fontSize: 11.5, color: 'var(--muted)', margin: '8px 2px 0', lineHeight: 1.45 }}>
            📍 Watches this tile’s live number{eventName ? <> for <b style={{ color: 'var(--text)' }}>{eventName}</b></> : null}, as the
            event has it set up — and only your own data. A filter you change on the dashboard right
            now isn’t part of the alert; for a slice like <i>Ticket Type = VIP</i>, build a filtered
            metric on the <Link to="/alerts" onClick={onClose} style={{ color: 'var(--brand)', fontWeight: 700 }}>Alerts</Link> page.
          </div>
          {err && <div style={{ color: 'var(--error, #dc2626)', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
        </>)}
      </div>

      {/* Sticky action bar — thumb-reachable on a phone, clear of the home bar. */}
      {!saved && canManage && !loadErr && (
        <div style={actions(isMobile)}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={save} disabled={busy} style={btnPrimary}>{busy ? 'Saving…' : 'Create alert'}</button>
        </div>
      )}
    </div>
  );

  return createPortal(
    <div style={overlay(isMobile)} onClick={onClose}>{body}</div>,
    document.body
  );
}

// ── The tile's number, read off the result already on screen ───────────────────
// Same field choice as SingleValueTile (honours hidden_fields, first visible
// measure/table-calc, then a dimension) so "Now:" always matches the big number.
// The unit is a GUESS from how the value renders (money symbol / %) with the tile
// title as a tiebreaker — the user can change it; it only affects wording.
export function tileMetric(tile, data) {
  const { dimensions, measures } = visibleColumns(data?.fields || {}, tile?.vis);
  const field = measures[0] || dimensions[0] || null;
  const row = (data?.data || [])[0] || null;
  let cell = field && row ? row[field.name] : null;
  // Pivoted result (e.g. this-event vs last-event columns): take the first column.
  if (cell && typeof cell === 'object' && !('value' in cell) && !('rendered' in cell)) cell = Object.values(cell)[0];
  const raw = cell && typeof cell === 'object' ? cell.value : cell;
  const n = Number(raw);
  const value = Number.isFinite(n) ? n : null;
  const display = cell ? cellText(cell) : '';
  const fmt = String(field?.value_format || '');
  const title = String(tile?.title || '');
  let unit = 'count';
  if (/^\s*[-+]?\s*(R|\$|€|£)/.test(display) || /[$€£]|\bR\b/.test(fmt) || /revenue|sales|gross|net|amount|value|spend|income/i.test(title)) unit = 'ZAR';
  else if (display.trim().endsWith('%') || fmt.includes('%') || /rate|percent|share|%/i.test(title)) unit = '%';
  else if (/ticket|scan|attend|guest|people|pax/i.test(title)) unit = 'tickets';
  else if (/order|transaction|basket/i.test(title)) unit = 'orders';
  return { value, display, unit };
}

function fmtNum(v, unit) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  const s = Math.abs(n) >= 1000 ? Math.round(n).toLocaleString('en-ZA') : String(n);
  if (unit === 'ZAR') return `R${s}`;
  if (unit === '%') return `${s}%`;
  return unit && unit !== 'count' ? `${s} ${unit}` : s;
}

function Field({ label, hint, children, style }) {
  return (
    <div style={{ marginBottom: 12, ...style }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 5 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, lineHeight: 1.4 }}>{hint}</div>}
    </div>
  );
}
function Seg({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      minHeight: 44, padding: '10px 10px', borderRadius: 10, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
      border: `1.5px solid ${active ? 'var(--brand)' : 'var(--hairline)'}`,
      background: active ? 'rgba(var(--brand-rgb,10,132,255),0.10)' : 'var(--card)',
      color: active ? 'var(--brand)' : 'var(--text)', fontFamily: 'inherit',
    }}>{children}</button>
  );
}
function Chip({ active, onClick, disabled, title, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} style={{
      minHeight: 40, padding: '9px 14px', borderRadius: 980, fontSize: 12.5, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
      border: `1.5px solid ${active ? 'var(--brand)' : 'var(--hairline)'}`, opacity: disabled ? 0.45 : 1,
      background: active ? 'rgba(var(--brand-rgb,10,132,255),0.10)' : 'var(--card)',
      color: active ? 'var(--brand)' : 'var(--text)', fontFamily: 'inherit',
    }}>{children}</button>
  );
}

// Full-screen on a phone; a centred card on desktop.
const overlay = (isMobile) => ({
  position: 'fixed', inset: 0, zIndex: 420, background: isMobile ? 'var(--bg, #fff)' : 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 16,
});
const sheet = (isMobile) => ({
  display: 'flex', flexDirection: 'column', width: '100%', color: 'var(--text)', background: 'var(--card)',
  ...(isMobile
    ? { height: '100%', borderRadius: 0 }
    : { maxWidth: 460, maxHeight: '90vh', borderRadius: 16, border: '1px solid var(--border)', boxShadow: 'var(--shadow-lg, 0 12px 40px rgba(0,0,0,0.28))' }),
});
const head = (isMobile) => ({
  display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
  padding: isMobile ? 'calc(12px + env(safe-area-inset-top)) 16px 12px' : '16px 18px 12px',
  borderBottom: '1px solid var(--border)',
});
const actions = (isMobile) => ({
  display: 'flex', gap: 10, flexShrink: 0, borderTop: '1px solid var(--border)', background: 'var(--card)',
  padding: isMobile ? '12px 16px calc(12px + env(safe-area-inset-bottom))' : '12px 18px 14px',
});
const inp = { width: '100%', boxSizing: 'border-box', minHeight: 44, padding: '10px 11px', border: '1.5px solid var(--hairline)', borderRadius: 9, fontSize: 14, outline: 'none', background: 'var(--card)', color: 'var(--text)', fontFamily: 'inherit' };
const xBtn = { border: 'none', background: 'rgba(128,128,128,0.12)', color: 'var(--muted-2)', borderRadius: 980, width: 32, height: 32, fontSize: 13, cursor: 'pointer', flexShrink: 0 };
const readsBox = { padding: '9px 11px', background: 'rgba(128,128,128,0.07)', borderRadius: 9, fontSize: 13, display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 };
const okBox = { padding: '12px 13px', background: 'rgba(16,185,129,0.10)', border: '1px solid var(--hairline)', borderRadius: 10 };
const miniBtn = { border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--brand)', borderRadius: 980, fontSize: 11.5, fontWeight: 700, padding: '5px 10px', cursor: 'pointer', flexShrink: 0 };
const discloseBtn = { border: 'none', background: 'transparent', color: 'var(--brand)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', padding: '6px 0', fontFamily: 'inherit' };
const sentenceBox = { marginTop: 12, padding: '11px 13px', background: 'rgba(var(--brand-rgb,10,132,255),0.07)', border: '1px solid var(--hairline)', borderRadius: 10, fontSize: 13, lineHeight: 1.5, fontWeight: 600 };
const btnGhost = { flex: '0 0 auto', minHeight: 44, padding: '11px 15px', borderRadius: 10, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
const btnPrimary = { flex: 1, minHeight: 44, padding: '11px 16px', borderRadius: 10, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' };
